// @refresh reset
import React, { useMemo, useRef, useState } from 'react';
import {
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  CloseIcon,
  Typography,
  BuilderChatSideIcon,
  OpenInNewIcon,
  TIconProps,
  Chip,
} from '@rbx/ui';
import { useTranslation, Locale } from '@rbx/intl';
import {
  NavigationTypeRaw,
  NavigationTypeRawLabelMap,
} from '@modules/utilities/pageBuild/types/NavigationRaw';
import baseUrl from '@modules/utilities/constants/baseUrl';
import DynamicallyRouted from '@modules/miscellaneous/common/components/DynamicallyRouted';
import Timestamp from '@modules/miscellaneous/common/components/Timestamp';
import {
  DocumentationDevForumSubType,
  DocumentationLuaType,
  DocumentationSubType,
} from '@modules/clients/docSiteSearchType';
import { isInteractKey } from '@modules/layout/layout/utils/keyboardNavigationHandler';
import { ESearchInteraction } from '@modules/eventStream/enum/DocsSiteSearch';
import useCurrentLocale from '@modules/localization/hooks/useCurrentLocale';
import {
  isRealMouseClickEvent,
  getModifierKeyInteraction,
  TResultClickedInteraction,
  isModifierKeyInteraction,
} from '@modules/search/searchEvents';
import { ESearchNavigationElement, useSearchNavigation } from './hooks/useSearchNavigation';
import useSearchListItemStyles from './SearchListItem.styles';
import { TSearchListItem } from './types/SearchListItem';
import {
  SvgIconAssistant,
  SvgIconCreate,
  SvgIconDocumentation,
  SvgIconLargePartBlock,
  SvgIconList,
  SvgIconPlayCircleOutlineFilled,
  SvgIconKeyboardReturn,
} from './searchIcons';

export const getSearchListItemLabelTranslated = (
  item: TSearchListItem,
  translate: ReturnType<typeof useTranslation>['translate'],
) => {
  if (!item.label) return '';
  if ('translationKey' in item.label) {
    return translate(item.label.translationKey, item.label.translationKeyParams);
  }
  return item.label.defaultLabel ?? '';
};

export const getSearchListItemTypeTranslated = (
  item: TSearchListItem,
  translate: ReturnType<typeof useTranslation>['translate'],
) => {
  return translate(NavigationTypeRawLabelMap[item.type] ?? 'Label.Article');
};

const getSearchListItemIcon = ({
  itemType,
  documentationSubType,
}: {
  itemType: TSearchListItem['type'];
  documentationSubType?: DocumentationSubType | null;
}) => {
  switch (itemType) {
    case NavigationTypeRaw.Assistant:
      return <SvgIconAssistant />;
    case NavigationTypeRaw.CloudAPI:
    case NavigationTypeRaw.CloudAPI2:
    case NavigationTypeRaw.CloudLegacy:
    case NavigationTypeRaw.CloudFeature:
      return <SvgIconCreate />;
    case NavigationTypeRaw.EngineAPI:
      if (documentationSubType === DocumentationLuaType.Enum) {
        // This covers both enums and enum items
        return <SvgIconList />;
      }
      return <SvgIconLargePartBlock />;
    case NavigationTypeRaw.EngineAPIMember:
      return <SvgIconLargePartBlock />;
    case NavigationTypeRaw.Forum:
      return <BuilderChatSideIcon />;
    case NavigationTypeRaw.Videos:
      return <SvgIconPlayCircleOutlineFilled />;
    case NavigationTypeRaw.Markdown:
    case NavigationTypeRaw.ReleaseNote:
    case NavigationTypeRaw.Lesson:
    default:
      return <SvgIconDocumentation />;
  }
};

export type EndAdornment = {
  Icon: React.FC<TIconProps>;
  label?: string;
  onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLButtonElement>) => void;
};

export type SearchListItemProps = {
  item: TSearchListItem;
  className?: string;
  endAdornment: EndAdornment;
  locale?: Locale;
  onKeyboardModifierDetected?: (interaction: TResultClickedInteraction) => void;
};

export type SearchListItemLinkProps = SearchListItemProps & {
  onClickItem: (item: TSearchListItem, interaction: TResultClickedInteraction) => void;
  onRemove?: (id: string) => void;
  ariaLabel?: string;
};

const getDefaultAriaLabel = (
  item: TSearchListItem,
  itemLabel: string,
  translate: ReturnType<typeof useTranslation>['translate'],
): string => {
  if (itemLabel) return itemLabel;
  return itemLabel
    ? (translate('Label.GoToPage', {
        type: getSearchListItemTypeTranslated(item, translate),
        label: itemLabel,
        title: item.title,
      }) as string)
    : `Go to ${item.type} page "${item.title}"`;
};

export const placeholderEndAdornment: EndAdornment = {
  Icon: () => null, // Invisible placeholder
};

export const SearchListItem = React.memo<SearchListItemProps>(
  ({ item, className, endAdornment, locale, onKeyboardModifierDetected }) => {
    const itemRef = useRef<HTMLLIElement>(null);
    const { onKeyDownSearch } = useSearchNavigation(itemRef);
    const [effectiveEndAdornment, setEffectiveEndAdornment] = useState<EndAdornment>(endAdornment);
    const { classes, cx } = useSearchListItemStyles({
      isSecondaryActionNonInteractive: !effectiveEndAdornment?.onClick,
    });
    const { translate } = useTranslation();
    const itemLabel = useMemo(
      () => getSearchListItemLabelTranslated(item, translate),
      [item, translate],
    );

    const secondaryContent = (() => {
      if (
        item.documentationSubType === DocumentationDevForumSubType.RobloxStaff ||
        item.documentationSubType === DocumentationDevForumSubType.CommunityResources ||
        item.documentationSubType === DocumentationDevForumSubType.CommunityTutorials
      ) {
        return (
          <Typography variant='body2' color='inherit' className={classes.listItemDescription}>
            {item.author?.name
              ? translate('Label.PostedBy', {
                  authorName: item.author?.name,
                })
              : ''}
          </Typography>
        );
      }

      // NOTE (neoxu, 2025-09-25): return timestamp only for DevForum content
      if (item.documentationSubType === DocumentationDevForumSubType.Announcements) {
        return (
          <Timestamp
            utcTime={item.updatedAtUtc}
            locale={locale ?? Locale.English}
            fallbackText={item.translatedCategoryDisplayText}
          />
        );
      }

      if (item.translatedCategoryDisplayText) {
        return (
          <Typography variant='body2' color='inherit' className={classes.listItemDescription}>
            {item.translatedCategoryDisplayText}
          </Typography>
        );
      }
      if (item.description) {
        return (
          <Typography
            variant='body2'
            color='inherit'
            className={classes.listItemDescription}
            dangerouslySetInnerHTML={{ __html: item.description }}
          />
        );
      }
      return null;
    })();

    const onKeyDown = (e: React.KeyboardEvent<HTMLLIElement>) => {
      const modifierInteraction = getModifierKeyInteraction(e, true);
      if (isInteractKey(e)) {
        // Prevent the keyboard event from bubbling up
        e.preventDefault();
        e.stopPropagation();

        // If modifier keys are pressed, open in new tab and track immediately
        if (
          isModifierKeyInteraction(modifierInteraction) &&
          onKeyboardModifierDetected &&
          item.path
        ) {
          // Track the modifier key interaction
          onKeyboardModifierDetected(modifierInteraction);

          // Open link in new tab using the item's path
          window.open(`${baseUrl}${item.path}`, '_blank', 'noopener,noreferrer');
          return;
        }

        // Regular click behavior (will be tracked in onClickItemLink as KeyboardEnter)
        itemRef.current?.click();
      } else {
        onKeyDownSearch(e);
      }
    };

    const onFocus = () => {
      setEffectiveEndAdornment({
        Icon: SvgIconKeyboardReturn,
      });
    };

    const onBlur = () => {
      // Always maintain an endAdornment to keep DOM structure consistent
      setEffectiveEndAdornment(endAdornment);
    };

    return (
      <ListItem
        key={item.id}
        ref={itemRef}
        tabIndex={0}
        data-search-navigation-element={ESearchNavigationElement.ListItem}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        componentsProps={{
          root: {
            className: cx(classes.listItem, className),
          },
        }}>
        <ListItemIcon className={classes.listItemIcon} aria-hidden='true'>
          {getSearchListItemIcon({
            itemType: item.type,
            documentationSubType: item.documentationSubType,
          })}
        </ListItemIcon>
        <ListItemText
          className={classes.listItemText}
          primaryTypographyProps={{
            color: 'inherit',
            className: classes.listItemPrimaryText,
          }}
          primary={
            <React.Fragment>
              <Typography
                variant={item.isTitleCode ? 'codeDense' : 'body2'}
                component='span'
                color='primary'
                className={classes.listItemTitle}
                aria-label={translate('Label.Title')}
                dangerouslySetInnerHTML={{ __html: item.title }}
              />
              {itemLabel && (
                <Typography
                  variant='body2'
                  component='span'
                  color='inherit'
                  className={classes.listItemLabel}
                  aria-label={translate('Label.Label')}>
                  {itemLabel}
                </Typography>
              )}
              <div className={classes.titleAdornmentWrapper}>
                {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                {/* @ts-ignore */}
                {item.deprecated && (
                  <Chip
                    label={translate('Label.Deprecated') || 'Deprecated'}
                    className={classes.titleAdornment}
                    size='small'
                    color='secondary'
                    variant='outlined'
                  />
                )}
              </div>
            </React.Fragment>
          }
          secondary={secondaryContent}
        />
        <ListItemSecondaryAction className={classes.listItemSecondaryAction}>
          {effectiveEndAdornment.onClick || effectiveEndAdornment.onKeyDown ? (
            <IconButton
              edge='end'
              size='small'
              color='inherit'
              aria-label={effectiveEndAdornment.label ?? ''}
              onClick={effectiveEndAdornment.onClick}
              onKeyDown={effectiveEndAdornment.onKeyDown}>
              <effectiveEndAdornment.Icon fontSize='small' color='inherit' aria-hidden='true' />
            </IconButton>
          ) : (
            <effectiveEndAdornment.Icon fontSize='small' color='inherit' aria-hidden='true' />
          )}
        </ListItemSecondaryAction>
      </ListItem>
    );
  },
);

SearchListItem.displayName = 'SearchListItem';

export const SearchListItemLink = React.memo<SearchListItemLinkProps>(
  ({ item, onClickItem, onRemove, className, ariaLabel, endAdornment }) => {
    let effectiveEndAdornment = endAdornment;
    const { translate } = useTranslation();
    const { targetLocale } = useCurrentLocale();

    // Handle keyboard modifier interactions that open new tabs
    const handleKeyboardModifierDetected = (interaction: TResultClickedInteraction) => {
      onClickItem(item, interaction);
    };

    const itemLabel = useMemo(
      () => getSearchListItemLabelTranslated(item, translate),
      [item, translate],
    );

    if (onRemove) {
      const handleRemoveClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.stopPropagation();
        e.preventDefault(); // Prevent the anchor navigation when clicking remove
        onRemove(item.id);
      };

      const handleRemoveKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
        if (isInteractKey(e)) {
          onRemove(item.id);
        }
      };

      effectiveEndAdornment = {
        Icon: CloseIcon,
        label: translate('Label.RemoveFromRecentlyVisited', { title: item.title }),
        onClick: handleRemoveClick,
        onKeyDown: handleRemoveKeyDown,
      };
    }

    const shouldOpenInNewTab = item.path?.startsWith('http');
    if (shouldOpenInNewTab) {
      effectiveEndAdornment = { Icon: OpenInNewIcon };
    }

    const onClickItemLink = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      if (isRealMouseClickEvent(e)) {
        // Check for modifier keys first
        const modifierInteraction = getModifierKeyInteraction(e, false);
        onClickItem(item, modifierInteraction); // track the click event with appropriate modifier
      } else {
        // This is a simulated click from keyboard (always regular Enter since modifiers open new tab)
        onClickItem(item, ESearchInteraction.KeyboardEnter); // track the keyboard enter event
      }
    };

    return (
      <DynamicallyRouted.Link
        tabIndex={-1}
        href={item.path}
        onClick={onClickItemLink}
        target={shouldOpenInNewTab ? '_blank' : undefined}
        rel={shouldOpenInNewTab ? 'noopener noreferrer' : undefined}
        aria-label={ariaLabel ?? getDefaultAriaLabel(item, itemLabel, translate)}
        style={{ textDecoration: 'none', color: 'inherit' }}>
        <SearchListItem
          item={item}
          className={className}
          endAdornment={effectiveEndAdornment}
          locale={targetLocale}
          onKeyboardModifierDetected={handleKeyboardModifierDetected}
        />
      </DynamicallyRouted.Link>
    );
  },
);

SearchListItemLink.displayName = 'SearchListItemLink';
